import {
  Button,
  ContentContainer,
  DocumentCheckbox,
  Header,
  Icon,
  LoadingSpinner,
  PdfViewer,
  Popup,
} from 'authflow-ui-engine'
import { observer } from 'mobx-react'
import React, { useContext, useEffect, useState } from 'react'
import { StoreContext } from '../../components/App'
import { Stepper } from '../../components/Stepper/Stepper'
import { StepperWrapper } from '../../components/Stepper/stepper.styles'
import { actionReport } from '../../methods/actionReport'
import { axiosInstance } from '../../methods/axiosConfig'
import { devLog } from '../../methods/devLog'
import {
  ButtonImgWrap,
  ButtonText,
  ConditionTitle,
  IntroParagraph,
  RequiredFooter,
} from '../../styles/generic.styles'
import {
  Divider,
  IconContainer,
  JitpayGeneralConditionsSpacerMobile,
  WelcomeCheck,
} from '../../styles/jitpay.styles'
import {
  ContentContainerBottomFixed,
  ContentContainerBottomLeft,
} from '../../styles/layout.styles'
import { TickAllStyle } from '../../styles/input.styles'
import {
  AcceptanceDocument,
  AcceptanceDocumentRes,
} from '../../types/acceptanceDocument'

export const GeneralConditionsJitpayQES = observer(() => {
  const store = useContext(StoreContext)
  const { pageWidth, pageHeight } = store.AppState
  const { theme } = store.InterfaceState
  const { currentPageIndex } = store.ScenarioState
  const { jitPayWelcomePage: trans, generalConditionsPage: transGeneral } =
    store.TranslationsState.translations

  // Checkboxes state
  const [terms, setTerms] = useState(false)
  const [privacyPolicy, setPrivacyPolicy] = useState(false)
  const [biometrics, setBiometrics] = useState(false)
  const [signed, setSigned] = useState(false)
  const [tickAll, setTickAll] = useState(false)

  // Popups visibility
  const [docPopupVisible, setDocPopupVisible] = useState(false)
  const [privPopupVisible, setPrivPopupVisible] = useState(false)
  const [biometricsPopupVisible, setBiometricsPopupVisible] = useState(false)

  // Documents fetched objects
  const [termsDoc, setTermsDoc] = useState<AcceptanceDocument>()
  const [policyDoc, setPolicyDoc] = useState<AcceptanceDocument>()
  const [biometricsDoc, setBiometricsDoc] = useState<AcceptanceDocument>()

  // Loadings state
  const [loading, setLoading] = useState(true)
  const [btnLoading, setBtnLoading] = useState(false)

  // Mobile utilities
  const [isMobile, setIsMobile] = useState(false)
  const [containerPdfViewerHeight, setContainerPdfViewerHeight] = useState('')

  useEffect(() => {
    if (pageWidth && pageWidth <= 980) {
      setContainerPdfViewerHeight(`calc(${window.innerHeight}px - 80px)`)
      setIsMobile(true)
    }
  }, [pageWidth, pageHeight])

  const getDocuments = async () => {
    try {
      const apiUrl = process.env.WEB_API_URL

      const res = await axiosInstance.get<AcceptanceDocumentRes>(
        `${apiUrl}/electronic-id/acceptance-document`,
        { withCredentials: true }
      )

      const privacyPolicy = res.data.find(
        (item) => item.type === 'PRIVACY_POLICY'
      )
      const termsAndConditions = res.data.find(
        (item) => item.type === 'TERMS_AND_CONDITIONS'
      )
      const biometrics = res.data.find(
        (item) => item.type === 'BIOMETRIC_CONSENT'
      )

      setTermsDoc(termsAndConditions)
      setPolicyDoc(privacyPolicy)
      setBiometricsDoc(biometrics)

      setLoading(false)
    } catch (e) {
      devLog(e)
    }
  }

  const handleTerms = ({ checked: value }) => {
    setTerms(value)
    actionReport({
      type: 'event.onboarding-web.consent.TERMS_AND_CONDITIONS_CHECKED',
      payload: {},
    })
  }

  const handlePrivacyPolicy = ({ checked: value }) => {
    setPrivacyPolicy(value)
    actionReport({
      type: 'event.onboarding-web.consent.PRIVACY_POLICY_CHECKED',
      payload: {},
    })
  }

  const handleBiometrics = ({ checked: value }) => {
    setBiometrics(value)
    actionReport({
      type: 'event.onboarding-web.consent.BIOMETRIC_CONSENT_CHECKED',
      payload: {},
    })
  }

  const handleTickAll = () => {
    setTickAll(!tickAll)
    setPrivacyPolicy(!tickAll)
    setBiometrics(!tickAll)
    setTerms(!tickAll)
  }

  const handleProceed = async () => {
    try {
      setBtnLoading(true)
      const apiUrl = process.env.WEB_API_URL
        ? process.env.WEB_API_URL
        : 'https://dev-02-web-api.attempt.signicat.io'

      await axiosInstance.post(
        `${apiUrl}/electronic-id/acceptance-document`,
        {
          acceptedDocuments: biometrics
            ? [policyDoc?.id, termsDoc?.id, biometricsDoc?.id]
            : [policyDoc?.id, termsDoc?.id],
        },
        {
          withCredentials: true,
        }
      )

      setBtnLoading(false)
      store.ScenarioState.setCurrentPage(currentPageIndex + 1)
    } catch (e) {
      devLog(e)
    }
  }

  useEffect(() => {
    if (terms === true && privacyPolicy === true) {
      actionReport({
        type: 'event.onboarding-web.consent.CONSENT_ACCEPTED',
        payload: {},
      })
    }

    setSigned(terms === true && privacyPolicy === true)
  }, [terms, privacyPolicy])

  useEffect(() => {
    if (window.location.pathname.split('/')[1] !== 'mock') {
      getDocuments()
    }
  }, [])

  const isTouchScrollDisabled = () => {
    if (docPopupVisible || privPopupVisible || biometricsPopupVisible) {
      return 'none'
    } else {
      return 'auto'
    }
  }

  useEffect(() => {
    if (docPopupVisible === true) {
      actionReport({
        type: 'event.onboarding-web.consent.TERMS_AND_CONDITIONS_DISPLAYED',
        payload: {},
      })
    }
  }, [docPopupVisible])

  useEffect(() => {
    if (privPopupVisible === true) {
      actionReport({
        type: 'event.onboarding-web.consent.PRIVACY_POLICY_DISPLAYED',
        payload: {},
      })
    }
  }, [privPopupVisible])

  useEffect(() => {
    if (biometricsPopupVisible === true) {
      actionReport({
        type: 'event.onboarding-web.consent.BIOMETRIC_CONSENT_DISPLAYED',
        payload: {},
      })
    }
  }, [biometricsPopupVisible])

  return (
    <ContentContainer
      {...theme.container}
      width="560px"
      paddingMobile="20px"
      marginMobile="0"
      touchAction={isTouchScrollDisabled()}
    >
      {loading ? (
        <LoadingSpinner
          width="68px"
          padding="100px 0"
          height="68px"
          {...theme.loadingSpinner}
        />
      ) : (
        <>
          <Header
            {...theme.header}
            fontFamily={theme.globals.fontFamilyHeadline}
          >
            {trans.heading}
          </Header>
          <StepperWrapper
            color={theme.stepper.borderColor}
            fontSizeTitle={theme.stepper.fontSizeTitle}
            fontSizeDesc={theme.stepper.fontSizeDesc}
          >
            <Stepper />
          </StepperWrapper>
          <IntroParagraph
            color={theme.globals.linkColor}
            colorHover={theme.globals.linkColorHover}
          >
            {trans.descriptionPartOne}
            <a
              href="https://eidas.ec.europa.eu/efda/tl-browser/#/screen/tl/ES/42"
              target="_blank"
              rel="noreferrer"
            >
              {trans.serviceProvider}
            </a>
            {trans.descriptionPartTwo}
          </IntroParagraph>

          <IconContainer>
            <svg
              version="1.1"
              id="JITpay"
              xmlns="http://www.w3.org/2000/svg"
              x="0"
              y="0"
              viewBox="0 0 135 50"
              xmlSpace="preserve"
              width="135px"
              height="50px"
            >
              <g id="logoName">
                <path
                  fill="#00aa13"
                  d="M11.22 19.29c0 1.47-.11 2.55-.34 3.19-.21.59-.57 1.05-1.12 1.4-.56.35-1.24.53-2.03.53-1.07 0-1.87-.32-2.45-.96-.6-.66-.94-1.97-1.02-3.89l-.02-.57-4.22.58-.02.45c-.05 2.8.6 4.96 1.94 6.41 1.35 1.46 3.3 2.2 5.8 2.2 1.67 0 3.15-.35 4.4-1.04 1.28-.7 2.22-1.73 2.81-3.07.57-1.3.86-3.12.86-5.43V.25h-4.6v19.04zM21.19.25h4.6v27.92h-4.6zM29 4.47h8.86v23.7h4.6V4.47h8.89V.25H29zM67.4 8.59c-1.35-.91-2.93-1.37-4.69-1.37-1.35 0-2.53.27-3.5.81-.58.32-1.12.74-1.63 1.25V7.66h-4.05v27.97h4.34v-8.71c.41.35.88.67 1.39.94.95.5 2.02.76 3.21.76 1.59 0 3.13-.46 4.57-1.35 1.45-.9 2.56-2.21 3.32-3.9.74-1.65 1.12-3.54 1.12-5.59 0-1.92-.34-3.71-1.01-5.32-.69-1.66-1.72-2.96-3.07-3.87zm-.34 9.16c0 2.46-.49 4.29-1.44 5.43-.95 1.13-2.05 1.68-3.37 1.68-1.29 0-2.37-.53-3.29-1.61-.93-1.09-1.4-2.85-1.4-5.22 0-2.37.51-4.19 1.5-5.4.99-1.2 2.08-1.78 3.33-1.78 1.27 0 2.33.54 3.26 1.66.94 1.13 1.41 2.9 1.41 5.24zM91.42 25.22c-.13-.78-.2-2.71-.2-5.73v-4.4c0-1.51-.06-2.54-.18-3.15-.21-.98-.59-1.81-1.13-2.47-.54-.66-1.39-1.21-2.51-1.63-1.09-.41-2.52-.62-4.24-.62-1.73 0-3.27.24-4.59.72-1.36.49-2.42 1.21-3.15 2.13-.72.91-1.24 2.11-1.55 3.56l-.12.55 4.25.58.12-.45c.32-1.25.81-2.13 1.44-2.6.64-.47 1.7-.71 3.13-.71 1.52 0 2.67.33 3.41.99.5.44.76 1.27.76 2.45 0 .08 0 .24-.01.48-1.23.38-3.05.72-5.44 1-1.34.16-2.33.33-3.01.51-.93.26-1.78.65-2.53 1.15-.77.52-1.4 1.22-1.87 2.08-.47.86-.72 1.83-.72 2.86 0 1.77.64 3.25 1.89 4.39 1.24 1.13 3.02 1.7 5.28 1.7 1.35 0 2.64-.23 3.82-.68.96-.36 1.95-.94 2.97-1.72.13.61.32 1.17.57 1.66l.14.29h4.66l-.41-.76c-.39-.69-.65-1.42-.78-2.18zm-4.58-6.6v.46c0 1.37-.16 2.4-.48 3.07-.42.87-1.06 1.55-1.97 2.05-.91.51-1.96.77-3.14.77-1.15 0-2.01-.25-2.58-.75-.55-.49-.82-1.08-.82-1.81 0-.48.12-.9.37-1.28.25-.39.6-.67 1.07-.88.52-.22 1.44-.43 2.74-.62 2.01-.27 3.62-.61 4.81-1.01zM103.83 19.35c-.32.87-.61 1.76-.88 2.66-.28-.94-.59-1.86-.91-2.73L97.87 7.66h-4.68l7.59 20.03c-.1.27-.18.48-.23.63-.56 1.58-.83 2.15-.96 2.35-.24.37-.53.65-.9.85-.63.35-1.7.45-3.26.03l-.74-.2.5 4.19.31.11c.83.29 1.61.43 2.3.43 1.15 0 2.16-.29 3-.85.83-.55 1.56-1.42 2.18-2.58.46-.84 1.07-2.3 1.88-4.48l7.67-20.5h-4.42l-4.28 11.68zM111.96 1.37h2.42v6.36h1.51V1.37h2.43V0h-6.36zM124.48 0l-1.73 5.66-1.76-5.57-.03-.09h-2.06v7.73h1.4V2.09l1.79 5.64h1.31l1.7-5.53v5.53h1.4V0z"
                ></path>
              </g>
              <g id="logoClaim">
                <path
                  fill="#E87722"
                  d="M56.3 47.21c-.42.06-.65.05-.77-.02a.4.4 0 01-.14-.15c-.01-.03-.05-.15-.05-.55v-3.66h1.11v-1.12h-1.11v-2.37l-1.35.82v1.55h-.81v1.12h.81v3.6c0 .68.05 1.13.15 1.35.1.23.28.42.52.56.24.14.57.21.99.21.25 0 .54-.03.86-.1l.12-.03-.2-1.22-.13.01zM61.75 41.86c-.37-.2-.81-.3-1.31-.3-.71 0-1.31.23-1.81.7v-3.02h-1.36v9.22h1.36v-3.68c0-.49.06-.88.17-1.15.11-.27.3-.47.56-.63.27-.16.55-.24.85-.24.4 0 .7.11.92.34.21.22.32.6.32 1.11v4.24h1.36v-4.24c0-.65-.08-1.16-.24-1.51-.17-.35-.44-.64-.82-.84zM70.09 45.37c0-.13.01-.23.01-.3 0-1.1-.29-1.97-.86-2.58-.57-.62-1.32-.93-2.22-.93-.93 0-1.7.32-2.28.95-.58.63-.88 1.51-.88 2.63 0 1.08.29 1.94.87 2.55.58.61 1.37.92 2.35.92.78 0 1.43-.19 1.94-.58.51-.38.86-.93 1.04-1.62l.04-.15-1.39-.17-.04.1c-.16.44-.37.76-.63.95-.26.19-.58.29-.95.29-.51 0-.92-.17-1.26-.53-.32-.33-.5-.81-.56-1.42h4.83l-.01-.11zm-4.76-1.04c.05-.47.23-.85.52-1.13.32-.32.71-.47 1.18-.47.52 0 .93.19 1.26.59.19.23.31.57.37 1.01h-3.33zM80.53 45.37c0-.13.01-.23.01-.3 0-1.1-.29-1.97-.86-2.58-.57-.62-1.32-.93-2.22-.93-.93 0-1.7.32-2.28.95-.58.63-.88 1.51-.88 2.63 0 1.08.29 1.94.87 2.55.58.61 1.37.92 2.35.92.78 0 1.43-.19 1.94-.58.51-.38.86-.93 1.04-1.62l.04-.15-1.39-.17-.04.1c-.16.44-.37.76-.63.95-.26.19-.58.29-.95.29-.51 0-.92-.17-1.26-.53-.32-.33-.5-.81-.56-1.42h4.83l-.01-.11zm-4.77-1.04c.05-.47.23-.85.52-1.13.32-.32.71-.47 1.18-.47.52 0 .93.19 1.26.59.19.23.31.57.37 1.01h-3.33zM87.2 47.53c-.05-.26-.07-.91-.07-1.92v-1.47c0-.5-.02-.84-.06-1.04-.07-.32-.19-.59-.37-.8-.18-.21-.45-.39-.82-.53-.36-.13-.83-.2-1.4-.2s-1.08.08-1.52.24c-.44.16-.79.39-1.03.7-.24.3-.41.69-.51 1.17l-.03.14 1.33.18.03-.11c.11-.43.28-.73.5-.89.22-.16.58-.25 1.07-.25.52 0 .91.11 1.17.34.18.16.27.44.27.85v.19c-.41.13-1.03.25-1.85.34-.44.05-.77.11-1 .17-.31.09-.58.21-.83.38s-.46.4-.61.68c-.15.28-.23.59-.23.93 0 .58.21 1.06.62 1.43.41.37.99.56 1.73.56.45 0 .87-.07 1.26-.22.33-.13.68-.33 1.03-.61.04.23.11.43.2.61l.04.07h1.45l-.11-.19c-.12-.25-.21-.5-.26-.75zm-1.44-2.27v.22c0 .46-.06.81-.16 1.04-.14.3-.37.53-.67.7a2.2 2.2 0 01-1.07.26c-.39 0-.69-.09-.89-.26a.822.822 0 01-.29-.64c0-.17.04-.31.13-.45s.21-.24.38-.31c.18-.08.49-.15.93-.21.69-.09 1.24-.21 1.64-.35zM92.72 44.78c-.31-.13-.87-.31-1.65-.52-.53-.15-.85-.24-.96-.28a.89.89 0 01-.37-.25.482.482 0 01-.11-.3c0-.18.09-.34.27-.48.19-.15.53-.22 1-.22.4 0 .7.09.91.25s.33.4.38.7l.02.13 1.34-.18-.02-.13c-.07-.44-.21-.79-.4-1.06-.2-.27-.5-.49-.91-.64-.4-.15-.86-.23-1.39-.23-.35 0-.68.05-.99.14-.31.09-.56.21-.74.34-.24.17-.44.4-.58.66-.14.26-.21.56-.21.87 0 .34.09.66.26.94.17.28.43.51.76.67.32.15.88.33 1.7.54.72.18.99.29 1.08.36.18.12.27.27.27.48 0 .24-.1.44-.32.62s-.57.28-1.04.28c-.47 0-.83-.11-1.09-.32-.25-.21-.41-.52-.46-.92l-.02-.13-1.35.21.02.13c.13.72.43 1.27.89 1.64.46.37 1.14.55 2.01.55.53 0 1-.09 1.42-.28.42-.19.76-.46.98-.8.23-.34.34-.72.34-1.11 0-.4-.1-.74-.29-1.02-.16-.29-.42-.5-.75-.64zM97.68 45.58c-.12.34-.23.68-.33 1.03-.11-.36-.22-.71-.35-1.05l-1.38-3.84h-1.47l2.51 6.63c-.04.1-.06.17-.08.23-.19.53-.28.72-.32.79-.08.13-.19.23-.31.3-.22.12-.59.16-1.12.01l-.19-.05.15 1.31.08.03c.27.09.53.14.75.14.37 0 .7-.09.98-.28.27-.18.51-.46.71-.84.15-.28.36-.76.62-1.49l2.53-6.78H99.1l-1.42 3.86zM110.27 46.36l-1.2-4.65h-1.33l-1.06 3.9c-.09.36-.16.64-.22.86l-1.31-4.76h-1.41l2.04 6.66.03.09h1.35l1.2-4.57.13.59 1.06 3.98h1.34l2.11-6.75h-1.34l-1.39 4.65zM119.19 47.53c-.05-.26-.07-.91-.07-1.92v-1.47c0-.5-.02-.84-.06-1.04-.07-.32-.19-.59-.37-.8-.18-.21-.45-.39-.82-.53-.36-.13-.83-.2-1.4-.2s-1.08.08-1.52.24c-.44.16-.79.39-1.03.7-.24.3-.41.69-.51 1.17l-.03.14 1.33.18.03-.11c.11-.43.28-.73.5-.89.22-.16.58-.25 1.07-.25.52 0 .91.11 1.17.34.18.16.27.44.27.85v.19c-.41.13-1.03.25-1.85.34-.44.05-.77.11-1 .17-.31.09-.58.21-.83.38s-.46.4-.61.68c-.15.28-.23.59-.23.93 0 .58.21 1.06.62 1.43.41.37.99.56 1.73.56.45 0 .87-.07 1.26-.22.33-.13.68-.33 1.03-.61.04.23.11.43.2.61l.04.07h1.45l-.11-.19c-.12-.25-.21-.5-.26-.75zm-1.44-2.27v.22c0 .46-.06.81-.16 1.04-.14.3-.37.53-.67.7a2.2 2.2 0 01-1.07.26c-.39 0-.69-.09-.89-.26a.822.822 0 01-.29-.64c0-.17.04-.31.13-.45s.21-.24.38-.31c.18-.08.49-.15.93-.21.69-.09 1.24-.21 1.64-.35zM124.83 41.71l-1.42 3.87c-.12.34-.23.68-.33 1.03-.11-.36-.22-.71-.35-1.05l-1.38-3.84h-1.47l2.51 6.63c-.04.1-.06.17-.08.23-.19.53-.28.72-.32.79-.08.13-.19.23-.31.3-.22.12-.59.16-1.12.01l-.19-.05.15 1.31.08.03c.27.09.53.14.75.14.37 0 .7-.09.98-.28.27-.18.51-.46.71-.84.15-.28.36-.76.62-1.49l2.53-6.78h-1.36z"
                ></path>
              </g>
            </svg>
          </IconContainer>

          <Divider margin="30px 0" />

          {termsDoc?.id && (
            <WelcomeCheck margin="0 0 10px 0">
              <ConditionTitle
                required
                asteriskColor={theme.globals.inputRequired.asteriskColor}
              >
                {termsDoc?.title}
              </ConditionTitle>
              <DocumentCheckbox
                inputName="terms-checkbox"
                description={termsDoc?.agreement}
                externalUrl={termsDoc?.s3Url}
                handleDecision={handleTerms}
                value={terms}
                {...theme.documentCheckbox}
                margin="0 0 30px 0"
                contentFontsize="14px"
                iconSize="26px"
                onClick={() => setDocPopupVisible(true)}
              />
            </WelcomeCheck>
          )}

          {policyDoc?.id && (
            <WelcomeCheck>
              <ConditionTitle
                required
                asteriskColor={theme.globals.inputRequired.asteriskColor}
              >
                {policyDoc?.title}
              </ConditionTitle>
              <DocumentCheckbox
                inputName="privacy-policy"
                description={policyDoc?.agreement}
                externalUrl={policyDoc?.s3Url}
                handleDecision={handlePrivacyPolicy}
                value={privacyPolicy}
                {...theme.documentCheckbox}
                margin="0 0 30px 0"
                contentFontsize="14px"
                iconSize="26px"
                onClick={() => setPrivPopupVisible(true)}
              />
            </WelcomeCheck>
          )}

          {biometricsDoc?.id && (
            <WelcomeCheck margin="0 0 10px 0">
              <ConditionTitle>{biometricsDoc?.title}</ConditionTitle>
              <DocumentCheckbox
                inputName="biometrics-checkbox"
                description={biometricsDoc?.agreement}
                externalUrl={biometricsDoc?.s3Url}
                handleDecision={handleBiometrics}
                value={biometrics}
                {...theme.documentCheckbox}
                margin="0 0 40px 0"
                contentFontsize="14px"
                iconSize="26px"
                onClick={() => setBiometricsPopupVisible(true)}
              />
            </WelcomeCheck>
          )}

          {isMobile ? <JitpayGeneralConditionsSpacerMobile /> : <></>}
          <ContentContainerBottomFixed>
            {!isMobile ? <ContentContainerBottomLeft /> : <></>}

            <RequiredFooter
              fontSize={theme.globals.inputRequired.textFontSize}
              color={theme.globals.inputRequired.textColor}
            >
              {transGeneral.required}
            </RequiredFooter>

            {/* <TickAllStyle
              width={isMobile ? '100%' : '224px'}
              paddingMobile="14px 10px"
            >
              <input
                id="tick-all"
                type="checkbox"
                name="tick-all"
                checked={tickAll}
              />
              <span className="label-text" onClick={handleTickAll}>
                {transGeneral.tickAll}
              </span>
            </TickAllStyle> */}

            <Button
              id="btn-proceed"
              onClick={handleProceed}
              {...theme.button}
              disabled={!signed}
              width={isMobile ? '100%' : '224px'}
              paddingMobile="14px 40px"
            >
              {btnLoading ? (
                <LoadingSpinner
                  width="16px"
                  {...theme.loadingSpinner}
                  padding="0 0"
                  thickness={2}
                />
              ) : (
                <>
                  <ButtonImgWrap>
                    <Icon size="18px" type="checkmark" />
                  </ButtonImgWrap>
                  <ButtonText>{trans.proceed}</ButtonText>
                </>
              )}
            </Button>
          </ContentContainerBottomFixed>
          {isMobile ? (
            <>
              <Popup
                visibility={docPopupVisible}
                handleVisibility={setDocPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={termsDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={348}
                  viewerWidth="100%"
                  viewerHeight={containerPdfViewerHeight}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>
              <Popup
                visibility={privPopupVisible}
                handleVisibility={setPrivPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={policyDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={348}
                  viewerWidth="100%"
                  viewerHeight={containerPdfViewerHeight}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>
              <Popup
                visibility={biometricsPopupVisible}
                handleVisibility={setBiometricsPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={biometricsDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={348}
                  viewerWidth="100%"
                  viewerHeight={containerPdfViewerHeight}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>
            </>
          ) : (
            <>
              <Popup
                visibility={docPopupVisible}
                handleVisibility={setDocPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={termsDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={728}
                  viewerWidth="100%"
                  viewerHeight={`calc(100vh - 405px)`}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>
              <Popup
                visibility={privPopupVisible}
                handleVisibility={setPrivPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={policyDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={728}
                  viewerWidth="100%"
                  viewerHeight={`calc(100vh - 405px)`}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>

              <Popup
                visibility={biometricsPopupVisible}
                handleVisibility={setBiometricsPopupVisible}
                {...theme.popup}
              >
                <PdfViewer
                  file={biometricsDoc?.s3Url}
                  {...theme.pdfViewer}
                  canvasWidth={728}
                  viewerWidth="100%"
                  viewerHeight={`calc(100vh - 405px)`}
                  initialScale={1}
                  margin="30px 0 0 0"
                  containerPadding="0px"
                  navigationSpacerColor="none"
                />
              </Popup>
            </>
          )}
        </>
      )}
    </ContentContainer>
  )
})
